/**
 * save.js — versioned localStorage persistence plus the wallet/economy.
 *
 * The economy lives here rather than in its own module because every balance
 * change is a save mutation; splitting them would mean two objects racing to
 * own the same three numbers. `save` owns the document, `economy` is the
 * typed façade over the wallet slice of it.
 *
 * Writes are debounced (SAVE_DEBOUNCE_MS) so a round that fires forty score
 * events performs one localStorage write, not forty.
 *
 * `?fresh=1` starts from a clean document without clobbering the stored one
 * until the first write.
 *
 * @module core/save
 */

import {
  SAVE_KEY,
  SAVE_SCHEMA_VERSION,
  SAVE_DEBOUNCE_MS,
  STARTING_TICKETS,
  STARTING_TOKENS,
  STARTING_STARS,
  STALL_IDS,
  EVENTS,
} from '../constants.js';
import { bus } from './events.js';

const MS_PER_DAY = 86_400_000;

/** @returns {number} Whole days since the Unix epoch, UTC. */
function realDayIndex() {
  return Math.floor(Date.now() / MS_PER_DAY);
}

/**
 * A brand-new save document.
 * Any field added here must also be handled in `migrate()`.
 * @returns {object}
 */
function freshDocument() {
  const now = new Date().toISOString();
  /** @type {Record<string, object>} */
  const stalls = {};
  for (const id of STALL_IDS) {
    stalls[id] = { plays: 0, bestScore: 0, totalScore: 0, totalTokens: 0, lastScore: 0 };
  }
  return {
    version: SAVE_SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,

    /** Added to the real day index. Test mode moves this to fake calendar time. */
    dayOffset: 0,

    wallet: {
      tickets: STARTING_TICKETS,
      tokens: STARTING_TOKENS,
      stars: STARTING_STARS,
    },

    stalls,

    /** Direct prizes (GDD 7.4) awarded straight into the personal space. */
    prizes: {
      plush: [],  // { id, kind: 'plush-small'|'plush-large', stallId, day }
      fish: [],   // { id, variant, name, stallId, day }
    },

    /** Prize-desk progress. Owned by the meta agent. */
    sets: {},     // { [setId]: { pieces: string[], completed: boolean } }

    cosmetics: {
      owned: [],
      equipped: { head: null, body: null, beak: null, feet: null, aura: null },
    },

    /** Daily login streak (GDD 8.7). Owned by the meta agent. */
    daily: { streak: 0, lastClaimDay: -1, claimedDays: [], calendarsCompleted: 0 },

    /** Weekly challenge draw + progress counters (GDD 8.2). Meta agent owns. */
    challenges: { key: '', list: [], counters: {} },

    /** Per-ISO-week best score per stall — the weekly leaderboard (GDD 7.6). */
    weekly: { key: '', best: {} },

    /** Personal space (GDD 9). Owned by the space agent. */
    space: {
      shelfSlots: 12,
      tankCapacity: 5,
      decor: [],
      /** Shelf style (GDD 9.2). */
      theme: 'wooden-fairground',
      /** Floor and wall style (GDD 9.4). */
      roomTheme: 'classic-wood',
      /** prizeId → shelf slot, so a curated shelf stays curated. */
      arrangement: {},
      /** The twenty most recent visitor reactions (GDD 9.4). */
      guestbook: [],
      music: 'carnival-day',
      unlockedThemes: ['wooden-fairground', 'classic-wood'],
    },

    settings: {
      comfortVignette: true,
      snapTurn: true,
      handedness: 'right',
      musicVolume: 0.6,
      sfxVolume: 0.85,
      /** GDD 12.4 — master switch; false hides the store entirely. */
      iapEnabled: true,
    },

    tutorial: { completed: false },

    stats: {
      sessions: 0,
      totalPlays: 0,
      ticketsSpent: 0,
      tokensEarned: 0,
      throwsMade: 0,
    },
  };
}

/**
 * Bring an older document up to the current schema.
 * Unknown-but-newer versions are left alone; missing keys are filled from a
 * fresh document so a partial save never crashes a subsystem.
 * @param {object} doc
 * @returns {object}
 */
function migrate(doc) {
  const base = freshDocument();
  if (!doc || typeof doc !== 'object') return base;

  // Deep-merge only the shapes we know about — never trust stored data blindly.
  const out = { ...base, ...doc, version: SAVE_SCHEMA_VERSION };
  out.wallet = { ...base.wallet, ...(doc.wallet || {}) };
  out.stalls = { ...base.stalls, ...(doc.stalls || {}) };
  for (const id of STALL_IDS) {
    out.stalls[id] = { ...base.stalls[id], ...(out.stalls[id] || {}) };
  }
  out.prizes = { ...base.prizes, ...(doc.prizes || {}) };
  out.prizes.plush = Array.isArray(out.prizes.plush) ? out.prizes.plush : [];
  out.prizes.fish = Array.isArray(out.prizes.fish) ? out.prizes.fish : [];
  out.cosmetics = { ...base.cosmetics, ...(doc.cosmetics || {}) };
  out.cosmetics.owned = Array.isArray(out.cosmetics.owned) ? out.cosmetics.owned : [];
  out.cosmetics.equipped = { ...base.cosmetics.equipped, ...(doc.cosmetics?.equipped || {}) };
  out.daily = { ...base.daily, ...(doc.daily || {}) };
  out.challenges = { ...base.challenges, ...(doc.challenges || {}) };
  out.challenges.list = Array.isArray(out.challenges.list) ? out.challenges.list : [];
  out.weekly = { ...base.weekly, ...(doc.weekly || {}) };
  out.weekly.best = (out.weekly.best && typeof out.weekly.best === 'object') ? out.weekly.best : {};
  out.space = { ...base.space, ...(doc.space || {}) };
  // The space sub-collections are the only nested containers in the document,
  // so a save written before they existed needs them restored by hand.
  out.space.decor = Array.isArray(out.space.decor) ? out.space.decor : [];
  out.space.guestbook = Array.isArray(out.space.guestbook) ? out.space.guestbook : [];
  out.space.unlockedThemes = Array.isArray(out.space.unlockedThemes)
    ? out.space.unlockedThemes
    : base.space.unlockedThemes.slice();
  out.space.arrangement = (out.space.arrangement && typeof out.space.arrangement === 'object')
    ? out.space.arrangement
    : {};
  out.settings = { ...base.settings, ...(doc.settings || {}) };
  out.tutorial = { ...base.tutorial, ...(doc.tutorial || {}) };
  out.stats = { ...base.stats, ...(doc.stats || {}) };
  out.sets = doc.sets && typeof doc.sets === 'object' ? doc.sets : {};
  return out;
}

class SaveSystem {
  constructor() {
    /** @type {object} */
    this.data = freshDocument();
    this._timer = null;
    this._persist = true;
    this._loaded = false;
  }

  /**
   * Load from localStorage (or start fresh).
   * @param {{ fresh?: boolean, persist?: boolean }} [opts]
   */
  load({ fresh = false, persist = true } = {}) {
    this._persist = persist;
    if (fresh) {
      this.data = freshDocument();
      this._loaded = true;
      bus.emit(EVENTS.SAVE_LOAD, { fresh: true, data: this.data });
      return this.data;
    }
    let raw = null;
    try {
      raw = localStorage.getItem(SAVE_KEY);
    } catch {
      // Private browsing / disabled storage — run in memory.
      this._persist = false;
    }
    if (raw) {
      try {
        this.data = migrate(JSON.parse(raw));
      } catch (err) {
        console.warn('[save] corrupt save discarded:', err);
        this.data = freshDocument();
      }
    } else {
      this.data = freshDocument();
    }
    this.data.stats.sessions += 1;
    this._loaded = true;
    bus.emit(EVENTS.SAVE_LOAD, { fresh: !raw, data: this.data });
    this.markDirty();
    return this.data;
  }

  /** @returns {boolean} Whether load() has run. */
  isLoaded() {
    return this._loaded;
  }

  /** Queue a debounced write. Call after any mutation of `save.data`. */
  markDirty() {
    if (this._timer) return;
    this._timer = setTimeout(() => {
      this._timer = null;
      this.flush();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Write to localStorage immediately. */
  flush() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    this.data.updatedAt = new Date().toISOString();
    if (!this._persist) return;
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
      bus.emit(EVENTS.SAVE_WRITE, { key: SAVE_KEY });
    } catch (err) {
      console.warn('[save] write failed:', err);
    }
  }

  /** Wipe the document and the stored copy. */
  reset() {
    this.data = freshDocument();
    try {
      localStorage.removeItem(SAVE_KEY);
    } catch { /* storage unavailable */ }
    bus.emit(EVENTS.SAVE_RESET, {});
    this.markDirty();
    return this.data;
  }

  /** @returns {string} The full document as JSON. */
  export() {
    return JSON.stringify(this.data, null, 2);
  }

  /**
   * Replace the document from exported JSON.
   * @param {string|object} json
   * @returns {boolean} Whether the import was accepted.
   */
  import(json) {
    try {
      const parsed = typeof json === 'string' ? JSON.parse(json) : json;
      this.data = migrate(parsed);
      this.flush();
      bus.emit(EVENTS.SAVE_LOAD, { fresh: false, imported: true, data: this.data });
      return true;
    } catch (err) {
      console.warn('[save] import failed:', err);
      return false;
    }
  }

  /** @returns {object} A deep copy — safe to hand to tests. */
  snapshot() {
    return JSON.parse(JSON.stringify(this.data));
  }

  /**
   * Current day index, including any test-mode offset.
   * All day-based clocks (login streak, prize timestamps) use this.
   * @returns {number}
   */
  today() {
    return realDayIndex() + (this.data.dayOffset || 0);
  }

  /**
   * Move the game's calendar forward. Used by `__cc.time.advanceDays`.
   * @param {number} n Days to advance (may be negative).
   */
  advanceDays(n) {
    this.data.dayOffset = (this.data.dayOffset || 0) + Math.trunc(n);
    this.markDirty();
    return this.today();
  }

  /**
   * Record the outcome of a round against a stall's stats.
   * @param {string} stallId
   * @param {{ score: number, tokens: number }} result
   */
  recordRound(stallId, { score = 0, tokens = 0 } = {}) {
    const s = this.data.stalls[stallId] ||
      (this.data.stalls[stallId] = { plays: 0, bestScore: 0, totalScore: 0, totalTokens: 0, lastScore: 0 });
    s.plays += 1;
    s.lastScore = score;
    s.totalScore += score;
    s.totalTokens += tokens;
    if (score > s.bestScore) s.bestScore = score;
    this.data.stats.totalPlays += 1;
    this.markDirty();
  }
}

/** The one save system. */
export const save = new SaveSystem();

// ─────────────────────────────────────────────────────────────────────────────
// Economy — the typed façade over save.data.wallet
// ─────────────────────────────────────────────────────────────────────────────

function emitWallet(reason, delta) {
  bus.emit(EVENTS.WALLET_CHANGE, {
    reason,
    delta,
    tickets: save.data.wallet.tickets,
    tokens: save.data.wallet.tokens,
    stars: save.data.wallet.stars,
  });
}

export const economy = {
  /** @returns {number} */
  tickets: () => save.data.wallet.tickets,
  /** @returns {number} */
  tokens: () => save.data.wallet.tokens,
  /** @returns {number} */
  stars: () => save.data.wallet.stars,

  /** @returns {{tickets:number, tokens:number, stars:number}} */
  wallet: () => ({ ...save.data.wallet }),

  /**
   * Can the player pay this many tickets?
   * @param {number} n
   * @returns {boolean}
   */
  canAfford(n) {
    return save.data.wallet.tickets >= n;
  },

  /**
   * Spend tickets. Refuses (returns false, changes nothing) if the player is
   * short — callers must check the return value before starting a round.
   * @param {number} n
   * @param {string} [reason]
   * @returns {boolean}
   */
  spendTickets(n, reason = 'stall') {
    const cost = Math.max(0, Math.round(n));
    if (save.data.wallet.tickets < cost) {
      bus.emit(EVENTS.INSUFFICIENT_TICKETS, { needed: cost, have: save.data.wallet.tickets, reason });
      return false;
    }
    save.data.wallet.tickets -= cost;
    save.data.stats.ticketsSpent += cost;
    save.markDirty();
    bus.emit(EVENTS.TICKETS_SPENT, { amount: cost, reason });
    emitWallet(reason, { tickets: -cost });
    return true;
  },

  /**
   * Spend redemption tokens at the prize desk.
   * @param {number} n
   * @param {string} [reason]
   * @returns {boolean}
   */
  spendTokens(n, reason = 'prize-desk') {
    const cost = Math.max(0, Math.round(n));
    if (save.data.wallet.tokens < cost) return false;
    save.data.wallet.tokens -= cost;
    save.markDirty();
    emitWallet(reason, { tokens: -cost });
    return true;
  },

  /**
   * Spend stars on shelf/tank upgrades.
   * @param {number} n
   * @param {string} [reason]
   * @returns {boolean}
   */
  spendStars(n, reason = 'upgrade') {
    const cost = Math.max(0, Math.round(n));
    if (save.data.wallet.stars < cost) return false;
    save.data.wallet.stars -= cost;
    save.markDirty();
    emitWallet(reason, { stars: -cost });
    return true;
  },

  /**
   * Add currency. Negative values are ignored — use the spend* methods.
   * @param {{ tickets?: number, tokens?: number, stars?: number }} amounts
   * @param {string} [reason]
   */
  grant({ tickets = 0, tokens = 0, stars = 0 } = {}, reason = 'grant') {
    const d = {
      tickets: Math.max(0, Math.round(tickets)),
      tokens: Math.max(0, Math.round(tokens)),
      stars: Math.max(0, Math.round(stars)),
    };
    save.data.wallet.tickets += d.tickets;
    save.data.wallet.tokens += d.tokens;
    save.data.wallet.stars += d.stars;
    if (d.tokens > 0) {
      save.data.stats.tokensEarned += d.tokens;
      bus.emit(EVENTS.TOKENS_AWARDED, { amount: d.tokens, reason });
    }
    save.markDirty();
    emitWallet(reason, d);
    return { ...save.data.wallet };
  },

  /**
   * Record a direct prize (GDD 7.4) into the save and announce it.
   * @param {{ kind: string, stallId?: string, variant?: string, name?: string }} prize
   * @returns {object} The stored prize record.
   */
  awardDirectPrize(prize) {
    const record = {
      id: `${prize.kind}-${save.today()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: prize.kind,
      stallId: prize.stallId || null,
      variant: prize.variant || null,
      name: prize.name || null,
      day: save.today(),
    };
    if (prize.kind === 'fish') save.data.prizes.fish.push(record);
    else save.data.prizes.plush.push(record);
    save.markDirty();
    bus.emit(EVENTS.DIRECT_PRIZE, record);
    return record;
  },
};
